import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TransportProvider } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import { MagneticProvider } from '@magnetic/provider'
import { ThemeProvider } from '@/lib/theme-context'
import { AuthProvider } from '@/lib/auth'
import type { Interceptor } from '@connectrpc/connect'
import { Code } from '@connectrpc/connect'
import App from './App'
import './index.css'

// Interceptor to handle 401 Unauthorized responses
const authInterceptor: Interceptor = (next) => async (req) => {
  try {
    return await next(req)
  } catch (error: unknown) {
    // Check if the error is a Connect error with Unauthenticated status (401)
    // ConnectError has a 'code' property that matches Code enum
    const errorCode = typeof error === 'object' && error !== null && 'code' in error
      ? (error as { code?: unknown }).code
      : undefined

    if (errorCode === Code.Unauthenticated || errorCode === 'unauthenticated') {
      console.log('Unauthenticated error detected (401), redirecting to login...')
      // Redirect to SSO login immediately
      window.location.href = '/auth/login'
      // Don't throw - we're redirecting anyway
      return Promise.reject(error)
    }
    throw error
  }
}

// Create Connect transport with credentials (cookies) and auth interceptor
const transport = createConnectTransport({
  baseUrl: import.meta.env.VITE_API_URL || window.location.origin,
  // Use 'same-origin' for cookie-based auth (v2 syntax)
  useBinaryFormat: false,
  interceptors: [authInterceptor],
})

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TransportProvider transport={transport}>
        <AuthProvider>
          <MagneticProvider
            magnetize={true}
            zIndex={true}
            toast={true}
            analytics={false}
          >
            <ThemeProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemeProvider>
          </MagneticProvider>
        </AuthProvider>
      </TransportProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
